import { Component, inject, input, InputSignal, output, OutputEmitterRef, signal, Signal, SimpleChanges, WritableSignal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { JobsService } from '../../../services/jobs.service';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../material/material.module';
import {
  BehaviorSubject,
  catchError,
  finalize,
  of,
  Subject,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppService } from '../../../services/app.service';

@Component({
  selector: 'app-details',
  imports: [CommonModule, MaterialModule, RouterModule],
  templateUrl: './details.component.html',
  styleUrl: './details.component.scss',
})
export class DetailsComponent {
  private readonly _api = inject(JobsService);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _appService = inject(AppService);

  availableCreds: Signal<number> = this._appService.availableCreds;
  job: InputSignal<any> = input();
  addCandidate: OutputEmitterRef<any> = output();
  copied: String[] = [];
  candidates: any[] = [];
  loading: WritableSignal<boolean> = signal(false)
  tabIndex: WritableSignal<number> = signal(0);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['job']) {
      //Stop previous polling if any
      this.stopBackgroundPolling();

      if (this.job()) {
        this.candidates = [];
        this.candidates$.subscribe((result) => {
          this.candidates = result.data || [];
        });
      }
    }
  }

  private candidatesSubject = new BehaviorSubject<any>({ data: [] });
  public candidates$ = this.candidatesSubject.asObservable();
  private stopPolling = new Subject<void>();
  private isPolling = false;

  fetchCandidates() {
    if (this.loading()) return;
    this.loading.set(true);
    this._api
      .getCandidates(this.job()?.id, {
        fields:
          'id,candidate_id,candidate_name,resume,interview_link,status,job_id',
      })
      .pipe(
        finalize(() => (this.loading.set(false))),
        tap((candidates: any) => {
          this.candidatesSubject.next(candidates);

          if (candidates?.data?.length > 0) {
            const missingLinks = candidates.data.filter(
              (candidate: any) => !candidate.interview_link
            );
            if (missingLinks.length > 0 && !this.isPolling) {
              console.log(
                `Starting background polling for ${missingLinks.length} missing interview links`
              );
              this.startBackgroundPolling();
            }
          }
        }),
        catchError((error: any) => {
          this._snackbar.open('Error fetching candidates');
          return of({ data: [] });
        })
      ).subscribe()
  }

  // Background polling function
  private startBackgroundPolling(): void {
    if (this.isPolling) return;

    this.isPolling = true;
    let attempts = 0;

    timer(0, 4000)
      .pipe(
        takeUntil(this.stopPolling),
        switchMap(() => {
          attempts++;
          console.log(
            `Background attempt ${attempts}: Checking for interview links...`
          );

          return this._api
            .getCandidates(this.job()?.id, {
              fields:
                'id,candidate_id,candidate_name,resume,interview_link,status,job_id',
            })
            .pipe(
              catchError((error) => {
                console.error('Background poll error:', error);
                return of({ data: [] });
              })
            );
        }),
        tap((candidates: any) => {
          if (candidates?.data?.length > 0) {
            this.candidatesSubject.next(candidates);

            const missingLinks = candidates.data.filter(
              (candidate: any) => !candidate.interview_link
            );

            if (missingLinks.length === 0 || attempts == 10) {
              console.log(
                'All candidates now have interview links! Stopping background polling.'
              );
              this.stopBackgroundPolling();
            } else {
              console.log(
                `${missingLinks.length} candidates still missing interview links`
              );
            }
          }
        }),
        takeWhile(() => this.isPolling && attempts < 30) // Safety limit (2 minutes at 4-second intervals)
      )
      .subscribe({
        complete: () => {
          console.log('Background polling completed or max attempts reached');
          this.isPolling = false;
        },
      });
  }

  stopBackgroundPolling(): void {
    if (this.isPolling) {
      this.stopPolling.next();
      this.stopPolling.complete();
      this.isPolling = false;
      console.log('Background polling stopped');
    }
  }

  cleanup(): void {
    this.stopBackgroundPolling();
    this.candidatesSubject.complete();
  }

  handleTabChange(tab: any) {
    this.tabIndex.set(tab.index);
    if (this.tabIndex() == 1) {
      this.fetchCandidates()
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'pending':
        return `bg-gray-50 text-gray-700`;
      case 'rejected':
        return `bg-red-50 text-red-700`;
      case 'hold':
        return `bg-yellow-50 text-yellow-700`;
      case 'accepted':
        return `bg-green-50 text-green-700`;
      case 'analyzing':
        return `bg-blue-50 text-blue-700`;
      case 'pending review':
        return `bg-gray-50 text-gray-700`;
      default:
        return `bg-gray-50 text-gray-700`;
    }
  }

  downloadResume(link: string) {
    const a = document.createElement('a');
    a.href = link;
    a.download = 'resume.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async copyText(text: string): Promise<boolean> {
    try {
      this.copied.push(text);
      await navigator.clipboard.writeText(text);
      setTimeout(() => {
        this.copied = this.copied.filter((item: any) => item !== text);
      }, 2000);
      return true;
    } catch (error) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();

      try {
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return true;
      } catch (err) {
        document.body.removeChild(textarea);
        return false;
      }
    }
  }

  ngOnDestroy() {
    this.cleanup();
  }
}
